import React from 'react';
import { ClientPortalLayout } from './ClientPortalLayout';
import { SparklesIcon, AlertTriangleIcon, TargetIcon, ZapIcon, UsersIcon } from 'lucide-react';
export function OffsiteBriefPage() {
  return <ClientPortalLayout data-id="element-414">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-id="element-415">
        {/* Hero Card */}
        <div className="bg-[#0B1B3B] rounded-2xl p-8 lg:p-12 relative overflow-hidden text-white shadow-lg" data-id="element-416">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 opacity-10 pointer-events-none" data-id="element-417">
            <SparklesIcon className="w-96 h-96" data-id="element-418" />
          </div>

          <div className="relative z-10 max-w-2xl" data-id="element-419">
            <div className="flex items-center gap-2 text-amber-500 mb-6" data-id="element-420">
              <SparklesIcon className="w-4 h-4" data-id="element-421" />
              <span className="text-xs font-bold uppercase tracking-widest" data-id="element-422">
                AI Generated Synthesis
              </span>
            </div>
            <h1 className="font-serif-display text-4xl lg:text-5xl mb-6 leading-tight" data-id="element-423">
              Helix Therapeutics: Strategic Alignment
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed" data-id="element-424">
              Based on 25 participant surveys and executive sponsor intake, this
              brief outlines the core themes, tensions, and recommended focus
              areas for your upcoming offsite.
            </p>
          </div>
        </div>

        {/* Core Themes & Goals */}
        <div data-id="element-425">
          <div className="flex items-center gap-3 mb-6" data-id="element-426">
            <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center" data-id="element-427">
              <div className="w-3 h-3 rounded-full border-2 border-amber-500" data-id="element-428" />
            </div>
            <h2 className="font-serif-display text-2xl text-slate-900" data-id="element-429">
              Core Themes & Goals
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-430">
            {[{
            title: 'Gene Therapy Investment',
            desc: 'Strong consensus (84%) that a unified thesis is required, but divergent views on resource allocation vs legacy pipeline.'
          }, {
            title: 'Clinical Trial Velocity',
            desc: 'General agreement that current regulatory review processes are too slow. Need for agile, cross-functional evaluation tools.'
          }, {
            title: 'Commercial Integration',
            desc: 'Desire to bring commercial perspectives earlier into the R&D lifecycle to ensure market viability.'
          }, {
            title: 'Leadership Cohesion',
            desc: 'Need to break down silos between scientific and business units to foster a unified executive front.'
          }].map((theme, i) => <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-id="element-431">
                <h3 className="font-serif-display text-lg text-slate-900 mb-2" data-id="element-432">
                  {i + 1}. {theme.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-433">
                  {theme.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* Key Tensions */}
        <div data-id="element-434">
          <div className="flex items-center gap-3 mb-6" data-id="element-435">
            <AlertTriangleIcon className="w-6 h-6 text-amber-500" data-id="element-436" />
            <h2 className="font-serif-display text-2xl text-slate-900" data-id="element-437">
              Key Tensions Identified
            </h2>
          </div>

          <div className="space-y-3" data-id="element-438">
            {['R&D feels Commercial is over-promising timelines to external stakeholders without understanding regulatory hurdles.', 'Lack of clarity on who holds final decision rights for pausing or accelerating Phase 2 trials.', 'Budget constraints are creating friction between legacy product support and new innovation initiatives.'].map((tension, i) => <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 border-l-4 border-l-rose-400 shadow-sm" data-id="element-439">
                <p className="text-sm text-slate-700" data-id="element-440">{tension}</p>
              </div>)}
          </div>
        </div>

        {/* Recommended Focus Areas */}
        <div data-id="element-441">
          <div className="flex items-center gap-3 mb-6" data-id="element-442">
            <TargetIcon className="w-6 h-6 text-amber-500" data-id="element-443" />
            <h2 className="font-serif-display text-2xl text-slate-900" data-id="element-444">
              Recommended Focus Areas
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-445">
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-id="element-446">
              <ZapIcon className="w-6 h-6 text-blue-500 mb-4" data-id="element-447" />
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-448">
                Agile Decision Framework
              </h3>
              <p className="text-sm text-slate-600" data-id="element-449">
                Establish clear RACI models for trial progression to eliminate
                bottlenecks.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-id="element-450">
              <UsersIcon className="w-6 h-6 text-emerald-500 mb-4" data-id="element-451" />
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-452">
                Cross-functional Empathy
              </h3>
              <p className="text-sm text-slate-600" data-id="element-453">
                Dedicated sessions for R&D and Commercial to shadow each other's
                constraints.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-id="element-454">
              <TargetIcon className="w-6 h-6 text-purple-500 mb-4" data-id="element-455" />
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-456">
                Resource Allocation
              </h3>
              <p className="text-sm text-slate-600" data-id="element-457">
                Workshop to finalize the 2027 budget split between legacy and
                innovation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ClientPortalLayout>;
}